import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext";
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Travel() {
  const { travelNotes, loading } = useWebData(); 

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); // 'Month', 'Year', 'All'
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Dynamic Year
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // Modal States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC (Supports All Date Formats)
  // ==========================================
  useEffect(() => {
    if (!travelNotes) return;

    let result = [...travelNotes];

    // A. Search Filter (Date included)
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(item => {
            const dateStr = item.date ? new Date(item.date).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";

            const row = `${item.userName} ${item.from} ${item.to} ${item.mode} ${item.purpose} ${item.distance}`.toLowerCase();
            
            // Search by Text OR Date (e.g. "29 Dec")
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // 🔥 B. Date Filter (Fixed for Mixed Formats like DD-MM-YYYY or YYYY-MM-DD)
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); // 0 = Jan, 11 = Dec

        result = result.filter(item => {
            const rawDate = item.date || ""; 
            if (!rawDate) return false;

            let y = 0;
            let m = -1;

            // Logic 1: Try Standard Date Object
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                y = d.getFullYear();
                m = d.getMonth();
            } 
            // Logic 2: Manual Parse (Agar Date Object fail ho jaye)
            // Supports: "29-12-2025" or "29/12/2025"
            else {
                const parts = rawDate.split(/[-/]/); // Split by - or /
                if (parts.length === 3) {
                    // Check if Year is at end (e.g. 29-12-2025)
                    if (parts[2].length === 4) {
                        y = parseInt(parts[2]);
                        m = parseInt(parts[1]) - 1; // Month is 0-indexed
                    }
                    // Check if Year is at start (e.g. 2025-12-29)
                    else if (parts[0].length === 4) {
                        y = parseInt(parts[0]);
                        m = parseInt(parts[1]) - 1;
                    }
                }
            }

            // Match Logic
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            
            return true;
        });
    }

    // C. Sort (Newest First)
    result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    setFiltered(result);
  }, [travelNotes, searchTerm, viewMode, selectedDate, selectedYear]);

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.date),
        Employee: i.userName,
        From: i.from,
        To: i.to,
        Mode: i.mode,
        Distance_Km: i.distance,
        Purpose: i.purpose
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TravelLogs");
    XLSX.writeFile(wb, `Travel_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "travel_notes", editData.id), {
              from: editData.from,
              to: editData.to,
              mode: editData.mode,
              distance: editData.distance,
              purpose: editData.purpose
          });
          alert("Travel Log Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this log?")) await deleteDoc(doc(db, "travel_notes", id));
  };

  // Helper: Month/Year Logic
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  // 🔥 Dynamic Years (2024 -> Future)
  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
    if(!iso) return "-";
    const date = new Date(iso);
    return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* --- HEADER & FILTERS --- */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#8e44ad", display:"flex", alignItems:"center"}}>
                🚲 Travel Logs <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length} Records)</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            
            {/* VIEW MODE TOGGLES */}
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {/* DATE CHANGER (Month Mode) */}
            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {/* YEAR DROPDOWN (Year Mode) */}
            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            {/* SUPER SEARCH */}
            <input type="text" placeholder="🔍 Search Date, Location, Mode..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#8e44ad", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>From</th>
                    <th style={thStyle}>To</th>
                    <th style={thStyle}>Mode</th>
                    <th style={thStyle}>Km</th>
                    <th style={thStyle}>Purpose</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="8" style={{padding:"20px", textAlign:"center", color:"#999"}}>No logs found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.date)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.userName}</td>
                    <td style={tdStyle}>{row.from}</td>
                    <td style={tdStyle}>{row.to}</td>
                    <td style={tdStyle}>
                        <span style={{padding:"2px 8px", borderRadius:"10px", backgroundColor:"#f3e5f5", fontSize:"11px", color:"#8e44ad", fontWeight:"bold"}}>{row.mode}</span>
                    </td>
                    <td style={{...tdStyle, fontWeight:"bold"}}>{row.distance}</td>
                    <td style={{...tdStyle, maxWidth:"150px", color:"#555"}}>{row.purpose}</td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View">👁️</button>
                            <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                            <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#8e44ad"}}>🚲 Log Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.8"}}>
                <p><strong>📅 Date:</strong> {formatDate(viewData.date)}</p>
                <p><strong>👤 Employee:</strong> {viewData.userName}</p>
                <div style={{display:"flex", justifyContent:"space-between", background:"#f9f9f9", padding:"10px", borderRadius:"6px", margin:"10px 0"}}>
                    <span>🛫 From: <strong>{viewData.from}</strong></span>
                    <span>🛬 To: <strong>{viewData.to}</strong></span>
                </div>
                <p><strong>🛣️ Mode:</strong> {viewData.mode} ({viewData.distance} Km)</p>
                <p><strong>📝 Purpose:</strong> {viewData.purpose}</p>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Log</h3>
            
            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>From</label>
                    <input type="text" value={editData.from} onChange={(e)=>setEditData({...editData, from:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>To</label>
                    <input type="text" value={editData.to} onChange={(e)=>setEditData({...editData, to:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Mode</label>
                    <select value={editData.mode} onChange={(e)=>setEditData({...editData, mode:e.target.value})} style={modalInput}>
                        <option value="Bike">Bike 🏍️</option>
                        <option value="Car">Car 🚗</option>
                        <option value="Bus">Bus 🚌</option>
                        <option value="Train">Train 🚂</option>
                    </select>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Distance (Km)</label>
                    <input type="number" value={editData.distance} onChange={(e)=>setEditData({...editData, distance:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Purpose</label>
            <input type="text" value={editData.purpose} onChange={(e)=>setEditData({...editData, purpose:e.target.value})} style={modalInput}/>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

// Icons
const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#8e44ad", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};  

export default Travel;